'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetMarketRow, useGetMarket } from '@morfi/backend-sdk-v2/client';
import { useChartMarketSnapshot } from '@/components/chart/use-chart-market-snapshot';
import { TradeForm, TradeFormBare, type TradeFormProps } from '@/components/trade/trade-form';
import { isPoolSnapshotUnavailable, useOnchainTradeData } from '@/queries/onchain-trade-data';
type ConnectedTradeFormProps = Omit<TradeFormProps, 'tradeData' | 'marketDataStale' | 'marketDataPending' | 'marketDataRefreshing' | 'onRetryMarketData'> & {
  readonly bare?: boolean;
  /**
   * The row the page holds while a resolution lands (presented as `trading`),
   * and `undefined` otherwise. The form projects pools from it for the whole
   * hold: the live row reads `graduated` without the winner's cp-swap pool
   * before the refetch lands (which fails the projection into "pool state
   * unavailable", also for a sheet first opened during the hold), and a stale
   * `trading` refetch the hold rides out would move the quotes.
   */
  readonly heldMarket?: GetMarketRow | undefined;
};

/** Keep executable pool reads local to the form, independent of the page snapshot. */
export function ConnectedMarketTradeForm(t0) {
  const $ = _c(11);
  let heldMarket;
  let props;
  if ($[0] !== t0) {
    ({
      heldMarket,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = heldMarket;
    $[2] = props;
  } else {
    heldMarket = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== props.marketAddress) {
    t1 = {
      pubkey: props.marketAddress
    };
    $[3] = props.marketAddress;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const query = useGetMarket(t1);
  const liveMarket = useChartMarketSnapshot(query.data);
  const market = heldMarket ?? liveMarket;
  let t2;
  if ($[5] !== market || $[6] !== props || $[7] !== query.isError || $[8] !== query.isFetching || $[9] !== query.refetch) {
    t2 = <MarketTradeFormSnapshot {...props} market={market} queryFailed={query.isError} queryFetching={query.isFetching} refetchMarket={query.refetch} />;
    $[5] = market;
    $[6] = props;
    $[7] = query.isError;
    $[8] = query.isFetching;
    $[9] = query.refetch;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
interface SnapshotProps extends ConnectedTradeFormProps {
  readonly market: GetMarketRow | undefined;
  readonly queryFailed: boolean;
  readonly queryFetching: boolean;
  readonly refetchMarket: ReturnType<typeof useGetMarket>['refetch'];
}
function MarketTradeFormSnapshot(t0) {
  const $ = _c(27);
  let bare;
  let market;
  let props;
  let queryFailed;
  let queryFetching;
  let refetchMarket;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      market,
      queryFailed,
      queryFetching,
      refetchMarket,
      bare,
      ...props
    } = t0);
    t1 = useOnchainTradeData;
    t2 = market?.pubkey;
    t3 = props.outcomes.length;
    t4 = props.outcomes.map(_temp);
    $[0] = t0;
    $[1] = bare;
    $[2] = market;
    $[3] = props;
    $[4] = queryFailed;
    $[5] = queryFetching;
    $[6] = refetchMarket;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    bare = $[1];
    market = $[2];
    props = $[3];
    queryFailed = $[4];
    queryFetching = $[5];
    refetchMarket = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
  }
  const pool = t1(t2, t3, t4, market);
  const Form = bare === true ? TradeFormBare : TradeForm;
  let t5;
  if ($[11] !== pool.data) {
    t5 = pool.data === undefined ? {} : {
      tradeData: pool.data
    };
    $[11] = pool.data;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== pool || $[14] !== queryFailed) {
    t6 = queryFailed || isPoolSnapshotUnavailable(pool);
    $[13] = pool;
    $[14] = queryFailed;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const t7 = market === undefined || pool.isPending;
  const t8 = pool.data !== undefined && (queryFetching || pool.isFetching);
  let t9;
  if ($[16] !== pool || $[17] !== refetchMarket) {
    t9 = () => {
      Promise.all([refetchMarket(), pool.refetch()]);
    };
    $[16] = pool;
    $[17] = refetchMarket;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== Form || $[20] !== props || $[21] !== t5 || $[22] !== t6 || $[23] !== t7 || $[24] !== t8 || $[25] !== t9) {
    t10 = <Form {...props} {...t5} marketDataStale={t6} marketDataPending={t7} marketDataRefreshing={t8} onRetryMarketData={t9} />;
    $[19] = Form;
    $[20] = props;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}
function _temp(outcome) {
  return {
    outcomeIndex: outcome.index,
    outcomeMint: outcome.mint
  };
}